import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MonitorIcon from "@mui/icons-material/Monitor";
import PeopleIcon from "@mui/icons-material/People";

const FacePortalSection: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const features = [
    "Automated Memorial Page generation for all claims",
    "Secure password protection for private services",
    "Smart moderation to prevent social commentary spam",
    "White-label options to keep your brand central",
  ];

  return (
    <Box
      id="face"
      component="section"
      sx={{
        py: { xs: 12, md: 15 },
        bgcolor: colors.primary[900],
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 8, md: 16 },
            alignItems: "center",
          }}
        >
          {/* Left Side - Mock Dashboard */}
          <Box sx={{ position: "relative" }}>
            {/* Main Dashboard Card */}
            <Paper
              elevation={24}
              sx={{
                bgcolor: colors.primary[100],
                borderRadius: 6,
                overflow: "hidden",
                border: `4px solid ${colors.primary[800]}`,
              }}
            >
              {/* Browser Chrome */}
              <Box
                sx={{
                  bgcolor: colors.primary[700],
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#EF4444",
                    borderRadius: "50%",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#F59E0B",
                    borderRadius: "50%",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: "#10B981",
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    fontSize: "0.625rem",
                    color: colors.primary[400],
                    fontFamily: "monospace",
                  }}
                >
                  FACE_PORTAL_DASHBOARD_v1.0
                </Typography>
              </Box>

              {/* Dashboard Content */}
              <Box
                sx={{
                  p: 4,
                  aspectRatio: "16/9",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {/* Top Row - 3 Cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  {[1, 2, 3].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        height: 48,
                        bgcolor: `${colors.primary[400]}50`,
                        borderRadius: 2,
                      }}
                    />
                  ))}
                </Box>

                {/* Loading Bars */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      height: 16,
                      bgcolor: `${colors.primary[400]}50`,
                      borderRadius: "50px",
                      width: "100%",
                    }}
                  />
                  <Box
                    sx={{
                      height: 16,
                      bgcolor: `${colors.primary[400]}50`,
                      borderRadius: "50px",
                      width: "75%",
                    }}
                  />

                  {/* Large Content Area */}
                  <Box
                    sx={{
                      height: 96,
                      bgcolor: `${colors.primary[400]}20`,
                      borderRadius: 3,
                      border: `1px solid ${colors.primary[400]}30`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 1,
                    }}
                  >
                    <MonitorIcon
                      sx={{
                        fontSize: 32,
                        color: `${colors.primary[400]}50`,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Floating Stats Card */}
            <Paper
              elevation={12}
              sx={{
                position: "absolute",
                bottom: -24,
                right: -24,
                bgcolor: colors.primary[900],
                p: 2,
                borderRadius: 4,
                border: `1px solid ${colors.primary[800]}`,
                display: { xs: "none", md: "block" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: `${colors.neutral[100]}20`,
                    p: 1,
                    borderRadius: "50%",
                  }}
                >
                  <PeopleIcon
                    sx={{
                      color: colors.neutral[100],
                      fontSize: 24,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 700,
                      color: colors.primary[100],
                    }}
                  >
                    1,240 Viewers
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: colors.primary[300],
                    }}
                  >
                    Live Global Access
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Right Side - Content */}
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: colors.primary[100],
                mb: 3,
                lineHeight: 1.3,
              }}
            >
              The{" "}
              <Box
                component="span"
                sx={{
                  color: colors.primary[400],
                  fontStyle: "italic",
                }}
              >
                F.A.C.E.
              </Box>{" "}
              Portal: <br />
              Funeral Assurance Company Exchange
            </Typography>

            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: colors.primary[300],
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              A centralized hub designed for your claims team. Deploy memorial
              pages and manage streaming services for policyholders with a
              single click.
            </Typography>

            <List sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {features.map((feature, index) => (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{ alignItems: "flex-start", p: 0 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <CheckCircleIcon
                      sx={{
                        fontSize: 24,
                        color: colors.primary[400],
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{
                      sx: {
                        color: colors.primary[100],
                        fontWeight: 600,
                        fontSize: "1rem",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FacePortalSection;
